/**
 * Captures JavaScript console logs for bug report export.
 * Override console methods to store logs in memory.
 */

import { captureException } from '@sentry/svelte'

export type LogEntry = {
  timestamp: string
  level: 'log' | 'info' | 'warn' | 'error' | 'debug'
  args: unknown[]
}

const MAX_LOGS = 2000
const logs: LogEntry[] = []

const originalConsole = {
  log: console.log.bind(console),
  info: console.info.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  debug: console.debug.bind(console),
}

const addLog = (level: LogEntry['level'], ...args: unknown[]) => {
  logs.push({
    timestamp: new Date().toISOString(),
    level,
    args: args.map(arg => {
      if (arg instanceof Error) {
        return { type: 'Error', message: arg.message, stack: arg.stack }
      }
      if (typeof arg === 'object' && arg !== null) {
        try {
          return JSON.parse(JSON.stringify(arg))
        } catch {
          return String(arg)
        }
      }
      return arg
    }),
  })
  if (logs.length > MAX_LOGS) {
    logs.splice(0, logs.length - MAX_LOGS)
  }
}

export const initConsoleLogger = () => {
  if (typeof window === 'undefined') return

  console.log = (...args: unknown[]) => {
    addLog('log', ...args)
    originalConsole.log(...args)
  }
  console.info = (...args: unknown[]) => {
    addLog('info', ...args)
    originalConsole.info(...args)
  }
  console.warn = (...args: unknown[]) => {
    addLog('warn', ...args)
    originalConsole.warn(...args)
  }
  console.error = (...args: unknown[]) => {
    addLog('error', ...args)
    originalConsole.error(...args)

    // Try to find an Error object in the arguments
    const error = args.find(arg => arg instanceof Error)

    if (error) {
      // If we found an Error object, capture it
      captureException(error)
    } else if (args.length > 0) {
      // If no Error object found, create one from the first argument
      const firstArg = args[0]
      const errorMessage = typeof firstArg === 'string' ? firstArg : String(firstArg)
      captureException(new Error(errorMessage), {
        extra: {
          consoleErrorArgs: args,
        },
      })
    }
  }
  console.debug = (...args: unknown[]) => {
    addLog('debug', ...args)
    originalConsole.debug(...args)
  }
}

const formatLogEntry = (entry: LogEntry): string => {
  const argsStr = entry.args
    .map(arg => {
      if (typeof arg === 'object') return JSON.stringify(arg, null, 2)
      return String(arg)
    })
    .join(' ')
  return `[${entry.timestamp}] [${entry.level.toUpperCase()}] ${argsStr}`
}

export const exportConsoleLogs = (): string => {
  const header = [
    'FIXEE Bug Report - Console Logs',
    `Generated: ${new Date().toISOString()}`,
    `User Agent: ${typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A'}`,
    `URL: ${typeof window !== 'undefined' ? window.location.href : 'N/A'}`,
    '---',
    '',
  ].join('\n')

  const logContent = logs.map(formatLogEntry).join('\n')
  return header + logContent
}

export const downloadConsoleLogs = (filename?: string): void => {
  const content = exportConsoleLogs()
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename || `fixee-bug-report-${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.txt`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
